import React from 'react'; 
import { 
  Drawer, 
  Box, 
  Typography, 
  IconButton, 
  Divider, 
  Link 
} from '@mui/material'; 
import CloseIcon from '@mui/icons-material/Close'; 
import LoaderComponent from 'components/Loader'; 
import { ReactComponent as ViewDetailsIcon } from 'assets/view-detail-image-blue.svg'; 
import { useTheme } from '@mui/material/styles';
 
interface IncidentDrawerProps { 
  open: boolean; 
  onClose: () => void; 
  data: any; 
  loading: boolean; 
  link: any; 
} 
 
const renderField = (
  label: string,
  value: string | React.ReactNode | null,
  colors: any
) => ( 
  <Box 
    mb={1} 
    sx={{ 
      display: 'grid', 
      gridTemplateColumns: '180px 1fr', 
      columnGap: '8px', 
      alignItems: 'start', 
    }} 
  > 
    <Typography 
      variant="caption" 
      sx={{ mt: 1 , color: colors.modal.subTitle, fontSize: '12px', fontWeight: 500 }} 
    > 
      {label} 
    </Typography> 
    <Typography 
      variant="body2" 
      sx={{ color: colors.modal.text, fontWeight: 600, fontSize: '14px', lineHeight: 2 }} 
    > 
      {value || '-'} 
    </Typography> 
  </Box> 
); 
 
const IncidentDrawer: React.FC<IncidentDrawerProps> = ({ 
  open, 
  onClose, 
  data = {}, 
  loading, 
  link 
}) => { 
  const theme = useTheme();
  const colors = theme.palette;
  const incident = data; 
 
  let content: React.ReactNode; 
 
  if (loading) { 
    content = <LoaderComponent />; 
  } else if (incident) { 
    content = ( 
      <> 
        {renderField( 
          'Incident ID', 
          <Link 
            href={link} 
            target="_blank" 
            color={colors.modal.sectionTitle}
            underline="none" 
            sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              fontWeight: 600, 
              fontSize: '14px', 
              textDecoration: 'none', 
              '&:hover': { textDecoration: 'none' }, 
            }} 
          > 
            {incident.number} 
            <ViewDetailsIcon style={{border:'2px', width: 25, height: 25 }}/> 
          </Link>,
          colors
        )} 
 
        {renderField('Priority', incident.priority, colors)} 
        {renderField('Status', incident.status, colors)} 
        {renderField('Short Description', incident.short_description, colors)} 
        {renderField('Configuration Item Name', incident.ciName, colors)} 
        {renderField('Assignment Group', incident.assignmentGroupName, colors)} 
        {renderField('Service Offering', incident.soName, colors)} 
        {renderField('Business Criticality', incident.u_bil, colors)} 
        {renderField('Service Classification', incident.serviceClassification, colors)} 
        {renderField('Open Date', incident.opened_at, colors)} 
        {renderField('Resolved Date', incident.resolved_at, colors)} 
        {renderField('Closed Date', incident.closed_at, colors)} 
        {renderField('Assigned To', incident.assignedTo?.name, colors)} 
        {renderField('Opened By', incident.openedBy?.name, colors)} 
        {renderField('Resolved By', incident.resolvedBy?.name, colors)} 
        {renderField('Closed By', incident.closedBy?.name, colors)} 
        {renderField('Vendor Name', incident.u_vendor_name, colors)} 
      </> 
    ); 
  } else { 
    content = <Typography sx={{ color: colors.modal.text }}>No data available.</Typography>; 
  } 
 
  return ( 
    <Drawer anchor="right" open={open} onClose={onClose}> 
      <Box 
        sx={{ 
          width: 390, 
          bgcolor: colors.modal.titleBg, 
          color: colors.modal.title, 
          p: 2, 
          height: '100%', 
          overflowY: 'auto', 
        }} 
      > 
        <Box display="flex" justifyContent="space-between" alignItems="center"> 
          <Typography 
            sx={{ 
              fontSize: '14px', 
              fontStyle: 'italic', 
              fontWeight: 500, 
              color: colors.modal.title, 
            }} 
          > 
            Incident Details 
          </Typography> 
          <IconButton onClick={onClose} sx={{ color: colors.modal.title }}> 
            <CloseIcon /> 
          </IconButton> 
        </Box> 
 
        <Divider sx={{ my: 1, borderColor: `${colors.modal.subTitle} !important` }} /> 
        {content} 
      </Box> 
    </Drawer> 
  ); 
}; 
 
export default IncidentDrawer;
